/**
 * Toggle visibility for given inline content slot.
 * @param   {HTMLElement} contentSlot    The content slot we are toggling visibility for.
 * @returns {void}
 */
function makeVisible(contentSlot) {
    const isHidden = contentSlot.attributes['hidden'];

    if (isHidden) {
      contentSlot.removeAttribute('hidden');
    }
}

/**
 * Is this HTML element a inline content slot
 * @param {HTMLElement} element The element we are checking.
 * @return {Boolean}
 */
export function isContentSlot(element) {
    return element.classList.contains('js-category__inline-content-slot');
}

/**
 * Positions a given inline content slot after the given product tile in the category grid.
 * @param   {HTMLElement} categoryGrid   The category grid element in the DOM.
 * @param   {HTMLElement} contentSlot    The inline content slot we are placing after the product tile.
 * @param   {HTMLElement} productTile    The productTile element in the DOM we are placing the content slot after.
 * @param   {Boolean} isBefore           Determines if the content slot get's placed before the product tile,
 *                                          default placement: after.
 * @returns {void}
*/
export function positionContentSlotProductTileAndMakeVisible(categoryGrid, contentSlot, productTile, isBefore = false) {
    if (isBefore) {
        categoryGrid.insertBefore(contentSlot, productTile);
    } else {
        categoryGrid.insertBefore(contentSlot, productTile.nextSibling);
    }
    makeVisible(contentSlot);
}

/**
 * Determine how many products make up a row on different screen sizes.
 * @returns {Number} The number of total products per row.
 */
export function determineNumberOfProductsPerRow() {
    const screenWidth = window.innerWidth;

    if (screenWidth >= 0 && screenWidth <= 767) {
        return 2;
    } else if (screenWidth >= 768 && screenWidth <= 1320) {
      // NOTE There is a one-off media query set in place for products on category on screen sizes 1321px and above.
        return 3;
    }
    return 4;
}

/**
 * Determine the banner size of the inline content slot given.
 * @param   {HTMLElement} contentSlot The content slot we are determining the banner size for.
 * @returns {String} Returns a string describing the banner size of the content slot.
 */
export function determineBannerSize(contentSlot) {
    const is2x1 = contentSlot.classList.contains('c-product-tile--two-by-one');

    if (is2x1) {
        return '2x1';
    }
    return '1x1';
}

export function getNumberOfSpacesTakenUp(categoryGridContents, insertionColumn) {
    if (insertionColumn === 0) { return 0; }

    const tilesBeforeInsertion = Array.prototype.slice.call(categoryGridContents, 0, insertionColumn - 1);
    return tilesBeforeInsertion.reduce((accumulator, tile) => {
        const tileSize = determineBannerSize(tile);
        let sizeFactor = 0;

        if (tileSize === '2x1') {
            sizeFactor = 2;
        } else {
            sizeFactor = 1;
        }
        return accumulator + sizeFactor;
    }, 0);
}
/**
 * Determines if the content slot is being placed in an area of the category grid that will break the flow.
 * @param   {HTMLElement} categoryGrid   The category grid element in the DOM.
 * @param   {HTMLElement} productTile    The product tile we will be placing the content slot after.
 * @returns {Number} A number representing a left or right adjustment. (1 or -1) default: 0;
 */
export function isContentSlotBeingPositionedWithoutIssue(
  categoryGrid,
  targetedTile,
  contentSlotBannerSize,
  targetPosition
  ) {
    const categoryGridContents = getVisibleContentsFrom(categoryGrid);
    const totalColumns = determineNumberOfProductsPerRow();
    let insertionColumn;
    let adjustment = 0;

    if (targetedTile) {
        while (isContentSlot(targetedTile.nextElementSibling) || isContentSlot(targetedTile) && targetPosition === -1) {
            targetedTile = targetedTile.nextElementSibling;
            adjustment = adjustment + 1;
        }

        insertionColumn = Array.prototype.indexOf.call(categoryGridContents, targetedTile) + 2;
        let spacesTakenUpBeforeInsertion = getNumberOfSpacesTakenUp(categoryGridContents, insertionColumn);

        const targetTileBannerSize = determineBannerSize(targetedTile);
        let previousTileBannerSize = '';

        if (targetedTile.previousElementSibling !== null && !targetedTile.previousElementSibling.hasAttribute('hidden')) {
            previousTileBannerSize = determineBannerSize(targetedTile.previousElementSibling);
        }

        const bannerSizes = {
            contentSlotBannerSize: contentSlotBannerSize,
            targetTileBannerSize: targetTileBannerSize,
            previousTileBannerSize: previousTileBannerSize
        };

        if (totalColumns === 2) {
            return twoColumnAdjustments(insertionColumn, spacesTakenUpBeforeInsertion, bannerSizes, adjustment, targetPosition);
        }

        if (totalColumns === 3) {
            return threeColumnAdjustments(insertionColumn, spacesTakenUpBeforeInsertion, bannerSizes, adjustment, targetPosition);
        }

        if (totalColumns === 4) {
            return fourColumnAdjustments(insertionColumn, spacesTakenUpBeforeInsertion, bannerSizes, adjustment, targetPosition);
        }
    }
}

function twoColumnAdjustments(insertionColumn, spacesTakenUpBeforeInsertion, bannerSizes, adjustment, targetPosition) {
    const contentSlotIs2x1 = bannerSizes.contentSlotBannerSize === '2x1';
    const targetTileIs1x1 = bannerSizes.targetTileBannerSize === '1x1';

    if (spacesTakenUpBeforeInsertion % 2 === 1) {
        if (contentSlotIs2x1 && targetTileIs1x1) {
            adjustment =  targetPosition === -1 && adjustment === 0 ? adjustment : adjustment - 1;
        }
    }
    return adjustment;
}

function threeColumnAdjustments(insertionColumn, spacesTakenUpBeforeInsertion, bannerSizes, adjustment, targetPosition) {
    const contentSlotIs1x1 = bannerSizes.contentSlotBannerSize === '1x1';
    const contentSlotIs2x1 = bannerSizes.contentSlotBannerSize === '2x1';
    const targetTileIs1x1 = bannerSizes.targetTileBannerSize === '1x1';
    const targetTileIs2x1 = bannerSizes.targetTileBannerSize === '2x1';
    const previousTileIs2x1 = bannerSizes.previousTileBannerSize === '2x1';
    const previousTileIs1x1 = bannerSizes.previousTileBannerSize === '1x1';

    if (insertionColumn % 3 === 0) {
        if (contentSlotIs2x1 && targetTileIs1x1 && previousTileIs1x1) {
            adjustment = adjustment - 1;
        }else if (contentSlotIs1x1 && targetTileIs2x1 && previousTileIs2x1) {
            adjustment = adjustment - 1;
        }
    } else if (insertionColumn % 3 === 2) {
        if (contentSlotIs2x1 && targetTileIs2x1) {
            adjustment = adjustment + 1;
        }
    } else if (spacesTakenUpBeforeInsertion % 3 === 2) {
        if (contentSlotIs2x1 && targetTileIs2x1) {
            adjustment = adjustment + 1;
        }
    }
    return adjustment;
}

function fourColumnAdjustments(insertionColumn, spacesTakenUpBeforeInsertion, bannerSizes, adjustment, targetPosition) {
    const contentSlotIs2x1 = bannerSizes.contentSlotBannerSize === '2x1';
    const targetTileIs1x1 = bannerSizes.targetTileBannerSize === '1x1';
    const targetTileIs2x1 = bannerSizes.targetTileBannerSize === '2x1';

    if (spacesTakenUpBeforeInsertion % 4 === 3) {
        if (contentSlotIs2x1 && targetTileIs1x1) {
            adjustment = adjustment - 1;
        } else if (contentSlotIs2x1 && targetTileIs2x1) {
            adjustment = adjustment === 0 ? adjustment + 1 : adjustment;
        }
    }
    return adjustment;
}

/**
 * Get all product tile in category grid and filter out the content slots.
 * @param   {HTMLElement} categoryGrid   The category grid element in the DOM.
 * @returns {Array} Returns an Array of DOM elements containing only product tiles.
 */
export function getVisibleContentsFrom(categoryGrid) {
    const categoryGridChildren = Array.from(categoryGrid.children);
    return categoryGridChildren.filter((child) => {
      return !child.hasAttribute('hidden');
    });
}

/**
 * Gets a product tile HTML element in position given.
 * @param   {Number} position   The index of the product tile on the category grid.
 * @param   {Number} [offset]   An offset we need to use to calculate the actual index.
 * @returns {HTMLElement} The product tile in the position given. If position is negative we get the first product tile.
 */
export function getTileInPosition(position, categoryGrid) {
    const categoryGridContents = getVisibleContentsFrom(categoryGrid);

    if ((position <= 0 && position > -2) || position === 0) {
        return categoryGridContents[0];
    } else {
        return categoryGridContents[position];
    }
}

/**
 * Position content slot on category grid.
 * @param   {HTMLElement} categoryGrid  The category grid element in the DOM.
 * @param   {HTMLElement} contentSlot   The content slot being injected.
 * @param   {HTMLElement} productTile   The product tile we are targeting
 * @param   {Number} contentPosition    The content slot numerical position.
 * @returns {void}
 */
export function positionContentSlot(categoryGrid, contentSlot, targetedTile, contentPosition) {
    const productsPerRow = determineNumberOfProductsPerRow();
    const is2x1SlotOnSmall = productsPerRow === 2 && determineBannerSize(contentSlot) === '2x1';
    const targetIs2x1Content = determineBannerSize(targetedTile) === '2x1' && isContentSlot(targetedTile);

    if (contentPosition < 0) {
        positionContentSlotProductTileAndMakeVisible(categoryGrid, contentSlot, targetedTile, true);
    } else if (contentPosition === 0 && is2x1SlotOnSmall) {
        if (targetIs2x1Content) {
            positionContentSlotProductTileAndMakeVisible(categoryGrid, contentSlot, targetedTile);
        } else {
            positionContentSlotProductTileAndMakeVisible(categoryGrid, contentSlot, targetedTile, true);
        }
    } else if (contentPosition === 0) {
        positionContentSlotProductTileAndMakeVisible(categoryGrid, contentSlot, targetedTile);
    } else if (contentPosition > 0) {
        positionContentSlotProductTileAndMakeVisible(categoryGrid, contentSlot, targetedTile);
    }
}

/**
 * Determine where we should place the inline content slot based off banner size and initial placement.
 * @param   {HTMLElement} contentSlot   The content slot DOM element.
 * @param   {Number} targetPosition     The position we are targetting.
 * @param   {HTMLElement} categoryGrid  The category grid DOM element.
 * @param   {Number} offset             The offset we should consider.
 * @param   {HTMLElement} [targetProductTile]    Optional product tile to target.
 * @returns {void}
 */
export function calculatePosition(
  contentSlot,
  targetPosition,
  categoryGrid,
  visibleSlots = 0,
  maxPositionIndex,
  targetProductTile = undefined
) {
    const bannerSize = determineBannerSize(contentSlot);
    targetProductTile = getTileInPosition(targetPosition, categoryGrid);

    // NOTE Prevent last item from being tampered with so we do not see duplicate content slots.
    if ((maxPositionIndex - visibleSlots) <= targetPosition) { return; }

    const positionNeedsModification = isContentSlotBeingPositionedWithoutIssue(
        categoryGrid,
        targetProductTile,
        bannerSize,
        targetPosition
    );

    if (positionNeedsModification) {
        targetPosition = targetPosition + positionNeedsModification;
        targetProductTile = getTileInPosition(targetPosition, categoryGrid);
    }

    if (targetProductTile) {
        positionContentSlot(categoryGrid, contentSlot, targetProductTile, targetPosition);
    }
}
